<!--
  AuthChoiceCard — the inline "create your account" method chooser (Google / Email)
  shown in the chat during the signup step, plus the "already have an account?" exit
  link. The CGUA legal mention lives upstream, in the signup intro bubble + cgua-consent
  step (CguaConsentCard) — acceptance is explicit and precedes this card. Presentational:
  emits the three intents; the orchestrator owns the sub-phase transitions +
  double-submit guard (via `disabled`).
-->
<template>
  <div class="auth-choice-card">
    <!--
      Error banner (e.g. after a Google `account_exists` bounce) sits ABOVE the choices so
      it's read BEFORE the visitor re-acts. Purely informative — no link; the login CTA
      stays the normal bottom line. The visitor can retry Google with a different account,
      use email, or log in below.
    -->
    <p v-if="errorMessage" class="auth-choice-error" role="alert">
      <span aria-hidden="true">⚠️ </span>{{ errorMessage }}
    </p>
    <button type="button" class="auth-btn auth-google" :disabled="disabled" @click="$emit('chooseGoogle')">
      <!-- eslint-disable-next-line vue/no-restricted-syntax -- Google's four-colour brand mark, not a monochrome icon -->
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      Continuer avec Google
    </button>
    <button type="button" class="auth-btn auth-email" :disabled="disabled" @click="$emit('chooseEmail')">
      <span class="auth-email-icon" aria-hidden="true">✉️</span>
      Continuer avec un email
    </button>
    <button type="button" class="auth-login-link" @click="$emit('alreadyHaveAccount')">
      Vous avez déjà un compte ? <span class="auth-login-link-cta">Connectez-vous</span>
    </button>
  </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{ disabled?: boolean; errorMessage?: string | null }>(), {
  disabled: false,
  errorMessage: null,
})
defineEmits<{ chooseGoogle: []; chooseEmail: []; alreadyHaveAccount: [] }>()
</script>

<style scoped>
.auth-choice-card {
  width: 92%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}
.auth-btn {
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    transform 0.08s;
}
.auth-btn:active {
  transform: scale(0.98);
}
.auth-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.auth-email-icon {
  font-size: 16px;
  line-height: 1;
}
.auth-google {
  background: var(--brand-default);
  border: 1.5px solid var(--brand-default);
  color: var(--white);
}
.auth-google:hover:not(:disabled) {
  background: var(--blue-800);
  border-color: var(--blue-800);
}
.auth-google svg {
  background: var(--white);
  border-radius: 50%;
  padding: 2px;
  box-sizing: content-box;
}
.auth-email {
  background: var(--white);
  border: 1.5px solid var(--navy-800);
  color: var(--navy-800);
}
.auth-email:hover:not(:disabled) {
  background: var(--navy-800);
  color: var(--white);
}
.auth-login-link {
  width: 100%;
  background: none;
  border: none;
  padding: 8px 0 0;
  font-size: 13px;
  color: var(--grey-500);
  font-family: inherit;
  cursor: pointer;
  text-align: center;
}
.auth-login-link-cta {
  color: var(--grey-500);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.auth-login-link:hover .auth-login-link-cta {
  color: var(--navy-800);
}
/* Top banner: a light-red alert strip above the choices (the flex `gap` handles spacing). */
.auth-choice-error {
  width: 100%;
  margin: 0;
  padding: 9px 12px;
  font-size: 13px;
  line-height: 1.4;
  color: #dc2626; /* off-palette error red — matches EmailFormCard's .email-form-error */
  background: rgba(220, 38, 38, 0.08);
  border-radius: 10px;
  text-align: center;
  box-sizing: border-box;
}
</style>
